import {
  ref, watch, reactive, computed,
} from '@vue/composition-api'
import moment from 'moment-timezone'
import { successNotification, errorNotification } from '@/utils/toast-notifications'
import { dateTimePickerToUTC, timeToUTC, UTCToUserTimeZone } from '@/utils/date-format'
import router from '@/router'

export default function useTemplateForm(props, ctx) {
  const rateOptions = [
    {
      text: 'Percentage',
      value: 'percentage',
    },
    {
      text: 'Fixed',
      value: 'fixed',
    },
  ]
  const templateFormRef = ref(null)
  const responseVersion = ref({})
  const version = ref({
    name: '',
    effective_date_time: new Date(),
    timezone: moment.tz.guess(),
    settings: {
      minimum_amount: null,
      tax_amount: null,
      open_meter_amount: null,
      admin_commission: null,
      estimates: {
        rate: 'percentage',
        value: null,
      },
      arrival: {
        amount_per_minute: null,
        free_waiting_time: null,
        maximum_time_threshold: null,
      },
      upfront_estimates: {
        active: false,
        minimum_distance_threshold: null,
        distance: {
          rate: 'percentage',
          minimum: null,
          maximum: null,
        },
        time: {
          rate: 'percentage',
          minimum: null,
          maximum: null,
        },
      },
      drop_zones: {},
      peak_factors: [],
      time_slicings: [],
      distance_slicings: [],
    },
  })
  const distanceLabeling = computed(() => (version.value.settings.upfront_estimates.distance?.rate === 'percentage' ? '(%)' : '(KM)'))
  const timeLabeling = computed(() => (version.value.settings.upfront_estimates.time?.rate === 'percentage' ? '(%)' : '(Minute)'))
  const convertTime = (fromTimeZone, toTimeZone) => {
    const factors = version.value.settings.peak_factors
    for (let i = 0; i < factors.length; i += 1) {
      for (let j = 0; j < factors[i].config.rules.length; j += 1) {
        const rule = factors[i].config.rules[j]
        factors[i].config.rules[j].from_hour = timeToUTC(rule.from_hour, fromTimeZone, toTimeZone).format('HH:mm')
        factors[i].config.rules[j].to_hour = timeToUTC(rule.to_hour, fromTimeZone, toTimeZone).format('HH:mm')
      }
    }
  }

  const effectiveDate = ref(null)
  const effectiveTime = ref(null)
  watch([effectiveDate, effectiveTime], ([dateVal, timeVal]) => {
    if (dateVal && timeVal) {
      version.value.effective_date_time = dateTimePickerToUTC(dateVal, timeVal, version.value.timezone).toDate()
    }
  }, { immediate: true })
  const fetchingTemplates = ref(false)
  if (ctx.root.$route.params.id) {
    fetchingTemplates.value = true
    ctx.root.$store.dispatch('templates/getTemplate', ctx.root.$route.params.id).then(res => {
      version.value = { ...res.data.data }
      responseVersion.value = { ...res.data.data }
      if (version.value.settings?.upfront_estimates && Object.keys(version.value.settings?.upfront_estimates).length) {
        if (version.value.settings?.upfront_estimates?.time?.rate === 'fixed') {
          version.value.settings.upfront_estimates.time.maximum /= 60
          version.value.settings.upfront_estimates.time.minimum /= 60
        }
      }
      if (!('upfront_estimates' in version.value.settings)) {
        version.value.settings.upfront_estimates = reactive({
          active: false,
          minimum_distance_threshold: null,
          distance: {
            rate: 'percentage',
            minimum: null,
            maximum: null,
          },
          time: {
            rate: 'percentage',
            minimum: null,
            maximum: null,
          },
        })
      } else {
        if (version.value.settings.upfront_estimates.distance === null) {
          version.value.settings.upfront_estimates.distance = {
            rate: 'percentage',
            minimum: null,
            maximum: null,
          }
        }
        if (version.value.settings.upfront_estimates.time === null) {
          version.value.settings.upfront_estimates.time = {
            rate: 'percentage',
            minimum: null,
            maximum: null,
          }
        }
      }
      convertTime('UTC', version.value.timezone)
      const userTime = UTCToUserTimeZone(version.value.effective_date_time, version.value.timezone)
      effectiveDate.value = userTime.toDate()
      effectiveTime.value = userTime.format('HH:mm')
    }).catch(err => {
      console.log(err)
    }).finally(() => {
      fetchingTemplates.value = false
    })
  }

  const zoneNames = ref({})
  const fetchingZones = ref(false)
  const servicesByZone = ref({})
  const fetchZoneData = async () => {
    try {
      fetchingZones.value = true
      zoneNames.value = await ctx.root.$store.dispatch('zones/fetchZoneNamesCached')
      servicesByZone.value = await ctx.root.$store.dispatch('zones/fetchZoneByServicesCached')
    } catch (error) {
      console.log(error)
    } finally {
      fetchingZones.value = false
    }
  }
  const submitting = ref(false)
  const selectedTemplate = ref({})

  const submitTemplate = async () => {
    if (submitting.value === true) return
    const valid = await templateFormRef.value.validate()
    if (!valid) return
    const temp = {
      ...version.value,
      settings: {
        ...version.value.settings,
        upfront_estimates: {
          ...version.value.settings.upfront_estimates,
          time: {
            ...version.value.settings.upfront_estimates.time,
          },
          distance: {
            ...version.value.settings.upfront_estimates.distance,
          },
        },
      },
    }
    try {
      submitting.value = true
      if (!temp.settings.upfront_estimates.active) {
        if (!temp.settings.upfront_estimates.minimum_distance_threshold) {
          temp.settings.upfront_estimates.minimum_distance_threshold = null
        }
        // Backend responds with data as null
        if (responseVersion.value.upfront_estimates?.time === null && !temp.settings.upfront_estimates.time.maximum && !temp.settings.upfront_estimates.time.minimum) {
          temp.settings.upfront_estimates.time = null
        }
        if (responseVersion.value.upfront_estimates?.distance === null && !temp.settings.upfront_estimates.distance.maximum && !temp.settings.upfront_estimates.distance.minimum) {
          temp.settings.upfront_estimates.distance = null
        }
        // Backend responds with valid data and the admin clears it
        if (!temp.settings.upfront_estimates.time.maximum && !temp.settings.upfront_estimates.time.minimum) {
          temp.settings.upfront_estimates.time = null
        }
        if (!temp.settings.upfront_estimates.distance.maximum && !temp.settings.upfront_estimates.distance.minimum) {
          temp.settings.upfront_estimates.distance = null
        }
      }

      // Convert to seconds
      if (temp.settings.upfront_estimates?.time?.rate === 'fixed') {
        temp.settings.upfront_estimates.time.maximum *= 60
        temp.settings.upfront_estimates.time.minimum *= 60
      }
      convertTime(version.value.timezone, 'UTC')
      if (props.isNew) {
        const resp = await ctx.root.$store.dispatch('templates/createTemplate', temp)
        const templateId = resp?.data?.data?.id
        successNotification('Template created successfully')
        router.push({ path: `/pricing-templates/edit/${templateId}` })
      } else {
        await ctx.root.$store.dispatch('templates/editTemplate', { templateId: ctx.root.$route.params.id, templateData: temp })
        successNotification('Template edited successfully')
        router.push({ path: '/pricing-templates/' })
      }
    } catch (error) {
      console.error(error)
      const errorMsg = error?.response?.data?.error?.detail?.error
      const message = error?.response?.data?.error?.detail?.message
      errorNotification(message || 'Failed')
      console.error(errorMsg)
    } finally {
      convertTime('UTC', temp.timezone)
      submitting.value = false
    }
  }

  const publishTemplate = async (item, event) => {
    if (event) {
      event.preventDefault()
    }
    selectedTemplate.value = item
    ctx.root.$bvModal.show('publish-template-modal')
  }

  fetchZoneData()

  return {
    submitTemplate,
    publishTemplate,
    fetchingZones,
    zoneNames,
    servicesByZone,
    submitting,
    rateOptions,
    version,
    effectiveDate,
    effectiveTime,
    templateFormRef,
    fetchingTemplates,
    selectedTemplate,
    distanceLabeling,
    timeLabeling,
  }
}
